import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import UploadField from "@/components/UploadField";
import PerksEditor, { perksToValue, valueToPerks } from "@/components/circle/PerksEditor";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, Loader2, Gem, Eye, Lock } from "lucide-react";

const BILLING = [
  { id: "one_time", labelKey: "mke.b_one_time" },
  { id: "monthly", labelKey: "mke.b_monthly" },
  { id: "yearly", labelKey: "mke.b_yearly" },
];
const PERIOD_NEXT = { month: "week", week: "total", total: "month" };
const DURATION_UNITS = [
  { id: "days", labelKey: "mke.unit_days", mult: 1 },
  { id: "weeks", labelKey: "mke.unit_weeks", mult: 7 },
  { id: "months", labelKey: "mke.unit_months", mult: 30 },
];

function daysToValueUnit(days) {
  if (!days || days <= 0) return { value: "", unit: "months" };
  if (days % 30 === 0) return { value: String(days / 30), unit: "months" };
  if (days % 7 === 0) return { value: String(days / 7), unit: "weeks" };
  return { value: String(days), unit: "days" };
}

export default function MeinKreisEdit() {
  const t = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const circleId = searchParams.get("circleId") || "";
  const isEdit = !!circleId;
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", cover_image_url: "", priceEuro: "", priceCredits: "",
    billing_period: "monthly", seat_limit: "", welcome_message: "",
    zoom_url: "", onboarding_video_url: "",
    visibility: "invite_only", published: true,
    limitSeats: false, limitTime: false, accessValue: "", accessUnit: "months",
  });
  const [perks, setPerks] = useState({ selected: {}, custom: [] });
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!isEdit || !user || user.role !== "coach") return;
    api.get("/circles").then(({ data }) => {
      const rows = Array.isArray(data) ? data : data.rows || [];
      const c = rows.find((x) => x.id === circleId);
      if (c) {
        const du = daysToValueUnit(c.access_duration_days);
        setForm({
          name: c.name || "",
          description: c.description || "",
          cover_image_url: c.cover_image_url || "",
          priceEuro: c.price_cents ? (c.price_cents / 100).toString() : "",
          priceCredits: c.price_credits ? String(c.price_credits) : "",
          billing_period: c.billing_period || "monthly",
          seat_limit: c.seat_limit ? String(c.seat_limit) : "",
          welcome_message: c.welcome_message || "",
          zoom_url: c.zoom_url || "",
          onboarding_video_url: c.onboarding_video_url || "",
          visibility: c.visibility === "visible" ? "visible" : "invite_only",
          published: c.published != null ? !!c.published : true,
          limitSeats: !!c.seat_limit,
          limitTime: !!c.access_duration_days,
          accessValue: du.value,
          accessUnit: du.unit,
        });
        setPerks(perksToValue(c.perks));
        setSessions(Array.isArray(c.included_sessions) ? c.included_sessions : []);
      }
    }).catch((err) => toast.error(apiError(err))).finally(() => setLoading(false));
  }, [isEdit, circleId, user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addSession = () => { if (sessions.length < 12) setSessions((s) => [...s, { title: "", count: 1, period: "month" }]); };
  const updateSession = (i, patch) => setSessions((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeSession = (i) => setSessions((s) => s.filter((_, idx) => idx !== i));

  const buildPayload = () => {
    const unitMult = (DURATION_UNITS.find((u) => u.id === form.accessUnit) || DURATION_UNITS[2]).mult;
    const accessDays = form.limitTime && form.accessValue ? Math.max(1, parseInt(form.accessValue, 10)) * unitMult : 0;
    return {
      name: form.name.trim(),
      description: form.description.trim(),
      circle_kind: "individual",
      cover_image_url: form.cover_image_url.trim() || null,
      price_cents: form.priceEuro ? Math.round(parseFloat(form.priceEuro) * 100) : 0,
      price_credits: form.priceCredits ? parseInt(form.priceCredits, 10) : 0,
      billing_period: form.billing_period,
      seat_limit: form.limitSeats && form.seat_limit ? parseInt(form.seat_limit, 10) : 0,
      access_duration_days: accessDays,
      visibility: form.visibility,
      published: form.published,
      perks: valueToPerks(perks),
      welcome_message: form.welcome_message.trim() || null,
      zoom_url: form.zoom_url.trim() || null,
      onboarding_video_url: form.onboarding_video_url.trim() || null,
      included_sessions: sessions
        .filter((s) => (s.title || "").trim())
        .map((s) => ({ title: s.title.trim(), count: Number(s.count) || 1, period: s.period || "month" })),
    };
  };

  const save = async () => {
    if (form.name.trim().length < 2) { toast.error(t("mke.err_name")); return; }
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/circles/${circleId}`, buildPayload());
      else await api.post("/circles", buildPayload());
      toast.success(isEdit ? t("mke.saved") : t("cir.created"));
      navigate("/app/coach/circles/mein-kreis");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (loading) return <div><Skeleton className="h-10 w-48 rounded-xl" /><Skeleton className="mt-4 h-96 rounded-2xl" /></div>;

  return (
    <div className="max-w-2xl">
      <Seo title={isEdit ? t("mke.seo_edit") : t("mke.seo_create")} noindex />
      <Link to="/app/coach/circles/mein-kreis" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="mk-back">
        <ChevronLeft className="h-4 w-4" /> {t("mk.title")}
      </Link>
      <PageHeader eyebrow={t("mk.eyebrow")} title={isEdit ? t("mke.title_edit") : t("mke.title_create")} subtitle={t("mke.subtitle")} />

      <div className="space-y-6">
        {/* Basics */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 text-sm font-semibold text-foreground">{t("mke.basics")}</p>
          <div className="space-y-3">
            <div>
              <Label htmlFor="mk-name">{t("mke.name")}</Label>
              <Input id="mk-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("cir.name_ph")} data-testid="mk-name" />
            </div>
            <div>
              <Label htmlFor="mk-desc">{t("mke.description")}</Label>
              <Textarea id="mk-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("mke.desc_ph")} data-testid="mk-desc" />
            </div>
            <div>
              <Label htmlFor="mk-cover-url">{t("mke.cover")}</Label>
              <div className="mt-1 space-y-2">
                <UploadField value={form.cover_image_url} onChange={(p) => set("cover_image_url", p)} kind="photo" accept="image/*" testId="mk-cover-pick" />
                <Input id="mk-cover-url" value={form.cover_image_url} onChange={(e) => set("cover_image_url", e.target.value)} placeholder={t("mke.cover_ph")} data-testid="mk-cover-url" />
              </div>
            </div>
          </div>
        </section>

        {/* Price & billing */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 text-sm font-semibold text-foreground">{t("mke.price_billing")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="mk-price">{t("mke.price_eur")}</Label>
              <Input id="mk-price" type="number" min="0" step="0.01" value={form.priceEuro} onChange={(e) => set("priceEuro", e.target.value)} placeholder="0" data-testid="mk-price" />
            </div>
            <div>
              <Label htmlFor="mk-price-credits">{t("mke.price_credits")}</Label>
              <div className="flex items-center gap-2">
                <Gem className="h-4 w-4 text-primary" />
                <Input id="mk-price-credits" type="number" min="0" step="1" value={form.priceCredits} onChange={(e) => set("priceCredits", e.target.value)} placeholder="auto" data-testid="mk-price-credits" />
              </div>
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("mke.price_hint")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {BILLING.map((b) => (
              <button key={b.id} type="button" onClick={() => set("billing_period", b.id)} data-testid={`mk-billing-${b.id}`}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.billing_period === b.id ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                {t(b.labelKey)}
              </button>
            ))}
          </div>
        </section>

        {/* Access limits */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 text-sm font-semibold text-foreground">{t("mke.access_section")}</p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{t("mke.limit_seats")}</p>
                <p className="text-xs text-muted-foreground">{t("mke.limit_seats_hint")}</p>
              </div>
              <Switch checked={form.limitSeats} onCheckedChange={(v) => set("limitSeats", v)} data-testid="mk-limit-seats" />
            </div>
            {form.limitSeats && (
              <div>
                <Label htmlFor="mk-seats">{t("mke.max_seats")}</Label>
                <Input id="mk-seats" type="number" min="1" step="1" value={form.seat_limit} onChange={(e) => set("seat_limit", e.target.value)} placeholder={t("mke.seats_ph")} data-testid="mk-seats" className="max-w-[160px]" />
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{t("mke.limit_time")}</p>
                <p className="text-xs text-muted-foreground">{t("mke.limit_time_hint")}</p>
              </div>
              <Switch checked={form.limitTime} onCheckedChange={(v) => set("limitTime", v)} data-testid="mk-limit-time" />
            </div>
            {form.limitTime && (
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label htmlFor="mk-access-value">{t("mke.duration")}</Label>
                  <Input id="mk-access-value" type="number" min="1" step="1" value={form.accessValue} onChange={(e) => set("accessValue", e.target.value)} placeholder={t("mke.duration_ph")} data-testid="mk-access-value" className="w-24" />
                </div>
                <div className="flex gap-1.5">
                  {DURATION_UNITS.map((u) => (
                    <button key={u.id} type="button" onClick={() => set("accessUnit", u.id)} data-testid={`mk-access-unit-${u.id}`}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.accessUnit === u.id ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                      {t(u.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Perks */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-1 text-sm font-semibold text-foreground">{t("mke.perks_title")}</p>
          <p className="mb-3 text-xs text-muted-foreground">{t("mke.perks_hint")}</p>
          <PerksEditor value={perks} onChange={setPerks} />
        </section>

        {/* Visibility & published */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 text-sm font-semibold text-foreground">{t("mke.visibility")}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <button type="button" onClick={() => set("visibility", "visible")} data-testid="mk-visibility-visible" className={`flex items-start gap-3 rounded-xl border p-3 text-left ${form.visibility === "visible" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
              <Eye className={`mt-0.5 h-5 w-5 ${form.visibility === "visible" ? "text-primary" : "text-muted-foreground"}`} />
              <div><p className="text-sm font-medium text-foreground">{t("mke.vis_public")}</p><p className="text-xs text-muted-foreground">{t("mke.vis_public_hint")}</p></div>
            </button>
            <button type="button" onClick={() => set("visibility", "invite_only")} data-testid="mk-visibility-invite-only" className={`flex items-start gap-3 rounded-xl border p-3 text-left ${form.visibility === "invite_only" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
              <Lock className={`mt-0.5 h-5 w-5 ${form.visibility === "invite_only" ? "text-primary" : "text-muted-foreground"}`} />
              <div><p className="text-sm font-medium text-foreground">{t("mke.vis_invite")}</p><p className="text-xs text-muted-foreground">{t("mke.vis_invite_hint")}</p></div>
            </button>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{t("mke.published")}</p>
              <p className="text-xs text-muted-foreground">{t("mke.published_hint")}</p>
            </div>
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="mk-published" />
          </div>
        </section>

        {/* Welcome / Zoom / Video */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 text-sm font-semibold text-foreground">{t("mke.welcome_links")}</p>
          <div className="space-y-3">
            <div>
              <Label htmlFor="mk-welcome">{t("mke.welcome")}</Label>
              <Textarea id="mk-welcome" value={form.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} placeholder={t("mke.welcome_ph")} data-testid="mk-welcome" />
            </div>
            <div>
              <Label htmlFor="mk-zoom-url">{t("mke.zoom")}</Label>
              <Input id="mk-zoom-url" value={form.zoom_url} onChange={(e) => set("zoom_url", e.target.value)} placeholder="https://zoom.us/…" data-testid="mk-zoom-url" />
            </div>
            <div>
              <Label htmlFor="mk-onboarding-video-url">{t("mke.onboarding_video")}</Label>
              <div className="mt-1 space-y-2">
                <UploadField value={form.onboarding_video_url} onChange={(p) => set("onboarding_video_url", p)} kind="video" accept="video/*" testId="mk-onboarding-video-upload" />
                <Input id="mk-onboarding-video-url" value={form.onboarding_video_url} onChange={(e) => set("onboarding_video_url", e.target.value)} placeholder={t("mke.video_ph")} data-testid="mk-onboarding-video-url" />
              </div>
            </div>
          </div>
        </section>

        {/* Included sessions */}
        <section className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">{t("mke.sessions")}</p>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={addSession} disabled={sessions.length >= 12} data-testid="mk-session-add">
              <Plus className="h-3.5 w-3.5" /> {t("mke.add")}
            </Button>
          </div>
          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("mke.no_sessions")}</p>
          ) : (
            <div className="space-y-2">
              {sessions.map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Input className="min-w-[160px] flex-1" value={s.title} onChange={(e) => updateSession(i, { title: e.target.value })} placeholder={t("mke.session_title_ph")} data-testid={`mk-session-${i}`} />
                  <Input className="w-20" type="number" min="1" value={s.count} onChange={(e) => updateSession(i, { count: e.target.value })} data-testid={`mk-session-count-${i}`} />
                  <Button variant="outline" size="sm" className="w-28" onClick={() => updateSession(i, { period: PERIOD_NEXT[s.period] || "month" })} data-testid={`mk-session-period-${i}`}>
                    {t(`mke.period_${s.period || "month"}`)}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeSession(i)} data-testid={`mk-session-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || form.name.trim().length < 2} className="gap-1.5 rounded-full px-6" data-testid="mk-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isEdit ? t("mke.save") : t("mke.create")}
          </Button>
        </div>
      </div>
    </div>
  );
}
